import { Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { CustomerInformationService, PrintingLabel } from './customer-information.service';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmRadioGroupComponent,
  HlmRadioComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../ui';

interface SenderAddress { label: string; value: string; }

const SENDER_MILAN: SenderAddress = {
  label: 'Milan',
  value: 'Segretaria 24 SRL Via Monte Napoleone 8 20121 Milano',
};
const SENDER_BERLIN_FRIEDRICHSTRASSE: SenderAddress = {
  label: 'Berlin Friedrichstraße',
  value: 'Office Club * Friedrichstraße 171 * 10117 Berlin',
};
const SENDER_BERLIN_HAUPTSTRASSE: SenderAddress = {
  label: 'Berlin Hauptstrasse',
  value: 'ebuero AG * Hauptstrasse 8 im Meisenbachhaus * 10827 Berlin',
};
const SENDER_ADDRESSES: SenderAddress[] = [
  SENDER_MILAN, SENDER_BERLIN_FRIEDRICHSTRASSE, SENDER_BERLIN_HAUPTSTRASSE,
];

function getDefaultSender(brandId: number | null): SenderAddress {
  if (brandId === 1 || brandId === 2 || brandId === 11) return SENDER_BERLIN_HAUPTSTRASSE;
  return SENDER_MILAN;
}

@Component({
  selector: 'cmt-print-label-dialog',
  standalone: true,
  imports: [
    ...HLM_DIALOG_SHELL,
    FormsModule,
    HlmBtnDirective, HlmIconComponent,
    HlmRadioGroupComponent, HlmRadioComponent,
    HlmSpinnerComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucidePrinter" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Print Address Label</h2>
          <p hlmDialogDescription>Select sender address and print a sticker-sized label.</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        @if (loading()) {
          <div class="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <hlm-spinner size="18" />
            <span>Loading address…</span>
          </div>
        } @else if (errorMsg()) {
          <hlm-alert tone="error">
            <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
            <span>{{ errorMsg() }}</span>
          </hlm-alert>
        } @else if (label()) {
          <!-- Sender selection (not printed) -->
          <div class="no-print">
            <p class="font-mono-feat mb-2 text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
              Sender address
            </p>
            <hlm-radio-group [value]="selectedSender().value" (valueChange)="onSenderChange($any($event))">
              @for (addr of senderAddresses; track addr.label) {
                <hlm-radio [value]="addr.value">
                  <span class="text-sm font-medium text-foreground">{{ addr.label }}</span>
                </hlm-radio>
              }
            </hlm-radio-group>
          </div>

          <!-- Printable label preview -->
          <div
            id="printable-label"
            class="rounded-lg border border-dashed border-border bg-card p-4 font-sans text-[12pt] leading-snug text-foreground"
          >
            <p class="sender-line mb-1.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs underline">{{ selectedSender().value }}</p>
            <p class="m-0">{{ label()!.companyName }}</p>
            <p class="m-0">{{ label()!.additionalInformation }}</p>
            <p class="m-0">{{ formatName(label()!) }}</p>
            <p class="m-0">{{ label()!.street }}</p>
            <p class="m-0">{{ label()!.zipCode }} {{ label()!.city }}</p>
            <p class="m-0">{{ label()!.countryName }}</p>
          </div>
        }
      </section>

      <footer hlmDialogFooter class="no-print">
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Close</button>
        <button hlmBtn (click)="print()" [disabled]="loading() || !!errorMsg()">
          <hlm-icon name="lucidePrinter" size="0.9rem" />
          Print
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
  styles: [`
    @media print {
      /* Strip every visual chrome from the dialog stack so only the
         printable label remains. Without this, the page-host backgrounds,
         CDK overlay backdrop, and hlm-dialog-surface drop-shadow all
         bleed into the print output as a soft gray box behind the label.

         Note: ::ng-deep is deprecated, but kept here intentionally — these
         selectors target Material/CDK chrome (mat-mdc-dialog-surface, cdk-
         overlay-pane, cdk-overlay-backdrop) that lives outside the component
         shadow boundary. Scoping is safe because the entire block sits inside
         @media print and only affects print rendering. */
      :host { all: initial; display: block; font-family: Arial, sans-serif; font-size: 12pt; }
      :host ::ng-deep hlm-dialog-surface,
      :host ::ng-deep .mat-mdc-dialog-surface,
      :host ::ng-deep .mat-mdc-dialog-container,
      :host ::ng-deep .cdk-overlay-pane,
      :host ::ng-deep .cdk-overlay-backdrop {
        background: none !important;
        box-shadow: none !important;
        border: none !important;
        border-radius: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        max-height: none !important;
        height: auto !important;
      }
      .no-print, hlm-dialog-header, [hlmDialogFooter] { display: none !important; }
      [id="printable-label"] {
        border: none !important; padding: 0 !important; margin: 0 !important;
        background: none !important; box-shadow: none !important;
      }
      /* Restore legacy sticker-sized sender line for printed output only.
         On screen the same line uses text-xs to scale with user font-size. */
      [id="printable-label"] .sender-line { font-size: 7pt !important; }
    }
  `],
})
export class PrintLabelDialogComponent implements OnInit {
  private readonly data = inject<{ customerId: number; brandId: number | null }>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<PrintLabelDialogComponent>);
  private readonly service = inject(CustomerInformationService);

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly label = signal<PrintingLabel | null>(null);

  readonly senderAddresses = SENDER_ADDRESSES;
  readonly selectedSender = signal<SenderAddress>(getDefaultSender(this.data.brandId));

  ngOnInit(): void {
    this.service.getPrintingLabel(this.data.customerId).subscribe({
      next: (r) => { this.label.set(r); this.loading.set(false); },
      error: () => { this.errorMsg.set('Failed to load printing label'); this.loading.set(false); },
    });
  }

  formatName(l: PrintingLabel): string {
    return [l.academicTitle, l.name, l.surname].filter(Boolean).join(' ');
  }

  onSenderChange(value: unknown): void {
    const addr = this.senderAddresses.find(a => a.value === value);
    if (addr) this.selectedSender.set(addr);
  }

  print(): void { window.print(); }
}
